import React, { useEffect, useState, useMemo } from "react";
import { Table, Alert, Collapse } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ReactDatePicker from "react-datepicker";
import ReactSelect from "react-select";

import { CompanyId, UserId, JobRole, USER_ROLE } from "../../constants";
import LocalstorageService from "../../helpers/localstorage-service";
import DashboardService from "../../services/dashboard";

import { setLoadingOverlay } from "../../store/slices/loadingOverlaySlice";

import Breadcrumbs from "../../components/Breadcrumb";
import LoadingOverlay from "../../components/LoadingOverlay";

import "./Dashboard.css";

const BANK_COLORS = ["#3498db", "#e74c3c", "#2ecc71", "#9b59b6", "#f39c12", "#1abc9c", "#e91e63", "#00bcd4"];

// Custom styles for react-select
const selectStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: '38px',
    cursor: 'pointer',
  }),
  container: (provided) => ({
    ...provided,
    flex: 1,
    minWidth: 0,
  }),
  valueContainer: (provided) => ({
    ...provided,
    cursor: 'pointer',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
};

function Dashboard() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { showLoadingOverlay } = useSelector((state) => state.LoadingOverlay);
  const isRTL = i18n.language === 'he';

  const [breadcrumbItems] = useState([
    { title: t("APPLICATION_TITLE"), link: "#" },
    { title: t("DASHBOARD.TITLE"), link: "#" },
  ]);

  const [roleId, setRoleId] = useState(USER_ROLE.EMPLOYEE);
  const [userName, setUserName] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedAgent, setSelectedAgent] = useState("");

  const [summary, setSummary] = useState({});
  const [missingTerminals, setMissingTerminals] = useState([]);
  const [depositsToday, setDepositsToday] = useState([]);
  const [weeklyDeposits, setWeeklyDeposits] = useState([]);
  const [weeklyDepositsByBank, setWeeklyDepositsByBank] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const [agentList, setAgentList] = useState([]);

  // State for collapsible sections - check if mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);
  const [isDailyDepositsOpen, setIsDailyDepositsOpen] = useState(!isMobile);
  const [isWeeklyDepositsOpen, setIsWeeklyDepositsOpen] = useState(!isMobile);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 480;
      setIsMobile(mobile);
      // Only auto-collapse when switching TO mobile, not when resizing on desktop
      if (mobile && window.innerWidth !== isMobile) {
        setIsDailyDepositsOpen(false);
        setIsWeeklyDepositsOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  useEffect(() => {
    const userRole = LocalstorageService.getItem(JobRole);
    setRoleId(userRole);
    const storedUser = LocalstorageService.getItem("user");
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        setUserName(user.firstName || user.userName || "");
      } catch (e) { setUserName(""); }
    }
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadDashboardData(); }, [selectedDate, selectedEmployee, selectedAgent]);

  async function loadDashboardData() {
    const companyId = LocalstorageService.getItem(CompanyId);
    const userId = LocalstorageService.getItem(UserId);
    const userRole = LocalstorageService.getItem(JobRole);

    const payload = {
      companyId: parseInt(companyId) || 0,
      userId: parseInt(userId) || 0,
      roleId: parseInt(userRole) || 4,
      selectedDate: selectedDate ? selectedDate.toISOString().split("T")[0] : null,
      employeeId: selectedEmployee ? parseInt(selectedEmployee) : null,
      agentId: selectedAgent ? parseInt(selectedAgent) : null,
    };

    dispatch(setLoadingOverlay(true));
    try {
      const response = await DashboardService.getData(payload);
      const { status, data } = response.data;
      if (status === 200 && data) {
        setSummary(data.summary || {});
        setMissingTerminals(data.missingTerminals || []);
        setDepositsToday(data.depositsToday || []);
        setWeeklyDeposits(data.weeklyDeposits || []);
        setWeeklyDepositsByBank(data.weeklyDepositsByBank || []);
        setEmployeeList(data.employeeList || []);
        setAgentList(data.agentList || []);
      }
    } catch (error) {
      console.error("Dashboard error:", error);
    } finally {
      dispatch(setLoadingOverlay(false));
    }
  }

  const formatCurrency = (value) => {
    if (!value && value !== 0) return "₪0";
    return new Intl.NumberFormat("he-IL", { style: "currency", currency: "ILS", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
  };

  const formatShortCurrency = (value) => {
    if (!value) return "-";
    return new Intl.NumberFormat("he-IL", { maximumFractionDigits: 0 }).format(Math.round(value));
  };

  const getDayName = (dateStr) => {
    const date = new Date(dateStr);
    const days = ["יום א'", "יום ב'", "יום ג'", "יום ד'", "יום ה'", "יום ו'", "שבת"];
    return days[date.getDay()];
  };

  const depositTotals = useMemo(() => {
    return depositsToday.reduce((acc, item) => ({
      eod: acc.eod + (item.eod || 0),
      fee: acc.fee + (item.fee || 0),
      loan: acc.loan + (item.loan || 0),
      invoice: acc.invoice + (item.invoice || 0),
      depositAmount: acc.depositAmount + (item.depositAmount || 0),
    }), { eod: 0, fee: 0, loan: 0, invoice: 0, depositAmount: 0 });
  }, [depositsToday]);

  const bankAccounts = useMemo(() => {
    const unique = [];
    const seen = new Set();
    weeklyDepositsByBank.forEach((item) => {
      if (!seen.has(item.bankAccountId)) {
        seen.add(item.bankAccountId);
        unique.push({ id: item.bankAccountId, name: item.bankAccountName, color: BANK_COLORS[unique.length % BANK_COLORS.length] });
      }
    });
    return unique;
  }, [weeklyDepositsByBank]);

  const weeklyChartData = useMemo(() => {
    const grouped = {};
    weeklyDepositsByBank.forEach((item) => {
      if (!grouped[item.depositDate]) grouped[item.depositDate] = { total: 0, banks: {} };
      grouped[item.depositDate].banks[item.bankAccountId] = item.depositAmount;
      grouped[item.depositDate].total += item.depositAmount;
    });
    return grouped;
  }, [weeklyDepositsByBank]);

  const maxWeeklyValue = useMemo(() => Math.max(...Object.values(weeklyChartData).map((d) => d.total), 1), [weeklyChartData]);

  const getTerminalLabel = () => {
    if (roleId === USER_ROLE.EMPLOYEE) return t("DASHBOARD.MY_TERMINALS");
    if (roleId === USER_ROLE.AGENT) return t("DASHBOARD.TERMINALS_IN_AGENCY");
    return t("DASHBOARD.TERMINALS_IN_COMPANY");
  };

  // React-Select options
  const employeeOptions = useMemo(() => [
    { value: "", label: t("DASHBOARD.ALL_EMPLOYEES") },
    ...employeeList.map((emp) => ({ value: emp.id.toString(), label: emp.name }))
  ], [employeeList, t]);

  const agentOptions = useMemo(() => [
    { value: "", label: t("DASHBOARD.ALL_AGENTS") },
    ...agentList.map((agent) => ({ value: agent.id.toString(), label: agent.name }))
  ], [agentList, t]);

  const selectedAgentOption = agentOptions.find(opt => opt.value === selectedAgent) || agentOptions[0];
  const selectedEmployeeOption = employeeOptions.find(opt => opt.value === selectedEmployee) || employeeOptions[0];

  const weeklyTotals = useMemo(() => ({
    eod: weeklyDeposits.reduce((a, b) => a + (b.eod || 0), 0),
    fee: weeklyDeposits.reduce((a, b) => a + (b.fee || 0), 0),
    loan: weeklyDeposits.reduce((a, b) => a + (b.loan || 0), 0),
    depositAmount: weeklyDeposits.reduce((a, b) => a + (b.depositAmount || 0), 0),
  }), [weeklyDeposits]);

  return (
    <React.Fragment>
      {showLoadingOverlay && <LoadingOverlay />}
      <Breadcrumbs title={t("DASHBOARD.TITLE")} breadcrumbItems={breadcrumbItems} />

      <div className="dashboard-container" dir={isRTL ? "rtl" : "ltr"}>
        <div className="dashboard-header">
          <div className="greeting">👋 {t("DASHBOARD.HELLO")}, <strong>{userName}</strong></div>
          <div className="date-picker">
            <label>📅 {t("DASHBOARD.SELECT_DATE")}:</label>
            <ReactDatePicker
              selected={selectedDate}
              onChange={(date) => setSelectedDate(date)}
              dateFormat="dd/MM/yyyy"
              className="form-control date-input"
              shouldCloseOnSelect={true}
            />
          </div>
        </div>

        {roleId !== USER_ROLE.EMPLOYEE && (
          <div className="filters">
            {(roleId === USER_ROLE.COMPANY_ADMIN || roleId === USER_ROLE.SUPER_ADMIN) && (
              <div className="filter-group">
                <label>{t("DASHBOARD.SELECT_AGENT")}:</label>
                <ReactSelect
                  value={selectedAgentOption}
                  onChange={(option) => {
                    setSelectedAgent(option ? option.value : "");
                    setSelectedEmployee("");
                  }}
                  options={agentOptions}
                  styles={selectStyles}
                  placeholder={t("DASHBOARD.ALL_AGENTS")}
                  noOptionsMessage={() => t("RSEL_NO_OPT")}
                  isClearable={false}
                  isRtl={isRTL}
                />
              </div>
            )}
            <div className="filter-group">
              <label>{t("DASHBOARD.SELECT_EMPLOYEE")}:</label>
              <ReactSelect
                value={selectedEmployeeOption}
                onChange={(option) => setSelectedEmployee(option ? option.value : "")}
                options={employeeOptions}
                styles={selectStyles}
                placeholder={t("DASHBOARD.ALL_EMPLOYEES")}
                noOptionsMessage={() => t("RSEL_NO_OPT")}
                isClearable={false}
                isRtl={isRTL}
              />
            </div>
          </div>
        )}

        <div className="stats-grid">
          <div className="stat-card blue"><div className="stat-value">{summary.totalTerminals || 0}</div><div className="stat-label">{getTerminalLabel()}</div></div>
          <div className="stat-card orange"><div className="stat-value">{summary.terminalsToLoad || 0}</div><div className="stat-label">{t("DASHBOARD.TERMINALS_TO_LOAD")}</div></div>
          <div className="stat-card green"><div className="stat-value">{summary.terminalsLoaded || 0}</div><div className="stat-label">{t("DASHBOARD.TERMINALS_LOADED")}</div></div>
        </div>

        {roleId !== USER_ROLE.EMPLOYEE && missingTerminals.length > 0 && (
          <Alert color="warning" className="warning-box">
            <span className="warning-icon">⚠️</span>
            <div className="warning-content">
              <h4>{missingTerminals.length} {t("DASHBOARD.MISSING_TERMINALS")}</h4>
              <ul className="warning-list">
                {missingTerminals.slice(0, 5).map((terminal, idx) => <li key={idx}>• {terminal.terminalNumber} - {terminal.locationName}</li>)}
                {missingTerminals.length > 5 && <li>... ועוד {missingTerminals.length - 5}</li>}
              </ul>
            </div>
          </Alert>
        )}

        <h3 className="section-title">💰 {t("DASHBOARD.LOADING_SUMMARY")}</h3>
        <div className="money-stats">
          <div className="money-card target"><div className="money-value">{formatCurrency(summary.totalLoadingAmount)}</div><div className="money-label">{t("DASHBOARD.TARGET_AMOUNT")}</div></div>
          <div className="money-card loaded"><div className="money-value">{formatCurrency(summary.totalAmountLoaded)}</div><div className="money-label">{t("DASHBOARD.LOADED_AMOUNT")}</div></div>
          <div className="money-card remaining"><div className="money-value">{formatCurrency(summary.remainingAmount)}</div><div className="money-label">{t("DASHBOARD.REMAINING_AMOUNT")}</div></div>
          <div className="money-card cash-remain"><div className="money-value">{formatCurrency(summary.cashRemain)}</div><div className="money-label">{t("DASHBOARD.CASH_REMAIN")}</div></div>
        </div>

        {roleId !== USER_ROLE.EMPLOYEE && depositsToday.length > 0 && (
          <>
            <h3 
              className="section-title collapsible" 
              onClick={() => setIsDailyDepositsOpen(!isDailyDepositsOpen)}
            >
              🏦 {t("DASHBOARD.DEPOSITS_TODAY")}
              <span className={`collapse-icon ${isDailyDepositsOpen ? 'open' : ''}`}>
                {isDailyDepositsOpen ? '▼' : '▶'}
              </span>
            </h3>
            <Collapse isOpen={isDailyDepositsOpen}>
              <div className="table-container desktop-table">
                <Table responsive>
                  <thead>
                    <tr>
                      {(roleId === USER_ROLE.COMPANY_ADMIN || roleId === USER_ROLE.SUPER_ADMIN) && <th>{t("ROLES.AGENT")}</th>}
                      <th>{t("DASHBOARD.BANK_ACCOUNT")}</th>
                      <th>{t("DASHBOARD.EOD")}</th>
                      <th>{t("DASHBOARD.FEE")}</th>
                      <th>{t("DASHBOARD.LOAN")}</th>
                      <th>{t("DASHBOARD.TOTAL")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {depositsToday.map((deposit, idx) => (
                      <tr key={idx}>
                        {(roleId === USER_ROLE.COMPANY_ADMIN || roleId === USER_ROLE.SUPER_ADMIN) && <td>{deposit.agentName}</td>}
                        <td>{deposit.bankAccountName}</td>
                        <td>{formatCurrency(deposit.eod)}</td>
                        <td>{formatCurrency(deposit.fee)}</td>
                        <td>{formatCurrency(deposit.loan)}</td>
                        <td>{formatCurrency(deposit.depositAmount)}</td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      {(roleId === USER_ROLE.COMPANY_ADMIN || roleId === USER_ROLE.SUPER_ADMIN) && <td></td>}
                      <td><strong>{t("DASHBOARD.TOTAL")}</strong></td>
                      <td><strong>{formatCurrency(depositTotals.eod)}</strong></td>
                      <td><strong>{formatCurrency(depositTotals.fee)}</strong></td>
                      <td><strong>{formatCurrency(depositTotals.loan)}</strong></td>
                      <td><strong>{formatCurrency(depositTotals.depositAmount)}</strong></td>
                    </tr>
                  </tbody>
                </Table>
              </div>
              <div className="mobile-cards">
                {depositsToday.map((deposit, idx) => (
                  <div className="mobile-card" key={idx}>
                    <div className="mobile-card-header">{deposit.agentName ? deposit.agentName + " - " + deposit.bankAccountName : deposit.bankAccountName}</div>
                    <div className="mobile-card-row"><span className="mobile-card-label">{t("DASHBOARD.EOD")}:</span><span className="mobile-card-value">{formatCurrency(deposit.eod)}</span></div>
                    <div className="mobile-card-row"><span className="mobile-card-label">{t("DASHBOARD.FEE")}:</span><span className="mobile-card-value">{formatCurrency(deposit.fee)}</span></div>
                    <div className="mobile-card-row"><span className="mobile-card-label">{t("DASHBOARD.LOAN")}:</span><span className="mobile-card-value">{formatCurrency(deposit.loan)}</span></div>
                    <div className="mobile-card-row"><span className="mobile-card-label">{t("DASHBOARD.TOTAL")}:</span><span className="mobile-card-value">{formatCurrency(deposit.depositAmount)}</span></div>
                  </div>
                ))}
                <div className="mobile-card total">
                  <div className="mobile-card-header">{t("DASHBOARD.TOTAL")}</div>
                  <div className="mobile-card-row"><span className="mobile-card-label">{t("DASHBOARD.EOD")}:</span><span className="mobile-card-value">{formatCurrency(depositTotals.eod)}</span></div>
                  <div className="mobile-card-row"><span className="mobile-card-label">{t("DASHBOARD.FEE")}:</span><span className="mobile-card-value">{formatCurrency(depositTotals.fee)}</span></div>
                  <div className="mobile-card-row"><span className="mobile-card-label">{t("DASHBOARD.LOAN")}:</span><span className="mobile-card-value">{formatCurrency(depositTotals.loan)}</span></div>
                  <div className="mobile-card-row"><span className="mobile-card-label">{t("DASHBOARD.TOTAL")}:</span><span className="mobile-card-value">{formatCurrency(depositTotals.depositAmount)}</span></div>
                </div>
              </div>
            </Collapse>
          </>
        )}

        {(roleId === USER_ROLE.COMPANY_ADMIN || roleId === USER_ROLE.SUPER_ADMIN) && weeklyDeposits.length > 0 && (
          <>
            <h3 
              className="section-title collapsible"
              onClick={() => setIsWeeklyDepositsOpen(!isWeeklyDepositsOpen)}
            >
              📊 {t("DASHBOARD.WEEKLY_DEPOSITS")}
              <span className={`collapse-icon ${isWeeklyDepositsOpen ? 'open' : ''}`}>
                {isWeeklyDepositsOpen ? '▼' : '▶'}
              </span>
            </h3>
            <Collapse isOpen={isWeeklyDepositsOpen}>
              <div className="chart-legend">
                {bankAccounts.map((bank) => <div className="legend-item" key={bank.id}><div className="legend-color" style={{ backgroundColor: bank.color }}></div><span>{bank.name}</span></div>)}
              </div>
              <div className="weekly-chart">
                {weeklyDeposits.map((day, idx) => {
                  const dayData = weeklyChartData[day.depositDate] || { total: 0, banks: {} };
                  const isToday = day.depositDate === selectedDate;
                  return (
                    <div className="chart-bar" key={idx}>
                      <div className="chart-value">{formatShortCurrency(day.depositAmount)}</div>
                      <div className={"stacked-bar" + (isToday ? " today" : "")}>
                        {bankAccounts.map((bank) => {
                          const bankAmount = dayData.banks[bank.id] || 0;
                          const height = (bankAmount / maxWeeklyValue) * 100 || 0;
                          return <div key={bank.id} className="bar-segment" style={{ height: height + "px", backgroundColor: bank.color }} title={bank.name + ": " + formatCurrency(bankAmount)}></div>;
                        })}
                      </div>
                      <div className="chart-label">{isToday ? <strong>{t("DASHBOARD.TODAY")}</strong> : getDayName(day.depositDate)}</div>
                    </div>
                  );
                })}
              </div>
              <div className="table-container desktop-table">
                <Table responsive>
                  <thead><tr><th>{t("DASHBOARD.DAY")}</th><th>{t("DATE")}</th><th>{t("DASHBOARD.EOD")}</th><th>{t("DASHBOARD.FEE")}</th><th>{t("DASHBOARD.LOAN")}</th><th>{t("DASHBOARD.TOTAL")}</th></tr></thead>
                  <tbody>
                    {weeklyDeposits.map((day, idx) => {
                      const isToday = day.depositDate === selectedDate;
                      return (
                        <tr key={idx} style={isToday ? { backgroundColor: "#e8f8f5" } : {}}>
                          <td>{isToday ? <strong>{t("DASHBOARD.TODAY")}</strong> : getDayName(day.depositDate)}</td>
                          <td>{new Date(day.depositDate).toLocaleDateString("he-IL")}</td>
                          <td>{formatCurrency(day.eod)}</td>
                          <td>{formatCurrency(day.fee)}</td>
                          <td>{formatCurrency(day.loan)}</td>
                          <td>{formatCurrency(day.depositAmount)}</td>
                        </tr>
                      );
                    })}
                    <tr className="total-row">
                      <td colSpan="2"><strong>{t("DASHBOARD.WEEKLY_TOTAL")}</strong></td>
                      <td><strong>{formatCurrency(weeklyTotals.eod)}</strong></td>
                      <td><strong>{formatCurrency(weeklyTotals.fee)}</strong></td>
                      <td><strong>{formatCurrency(weeklyTotals.loan)}</strong></td>
                      <td><strong>{formatCurrency(weeklyTotals.depositAmount)}</strong></td>
                    </tr>
                  </tbody>
                </Table>
              </div>
              <div className="mobile-cards">
                {weeklyDeposits.map((day, idx) => {
                  const isToday = day.depositDate === selectedDate;
                  return (
                    <div className={`mobile-card ${isToday ? 'today-card' : ''}`} key={idx}>
                      <div className="mobile-card-header">
                        {isToday ? t("DASHBOARD.TODAY") : getDayName(day.depositDate)} - {new Date(day.depositDate).toLocaleDateString("he-IL")}
                      </div>
                      <div className="mobile-card-row"><span className="mobile-card-label">{t("DASHBOARD.EOD")}:</span><span className="mobile-card-value">{formatCurrency(day.eod)}</span></div>
                      <div className="mobile-card-row"><span className="mobile-card-label">{t("DASHBOARD.FEE")}:</span><span className="mobile-card-value">{formatCurrency(day.fee)}</span></div>
                      <div className="mobile-card-row"><span className="mobile-card-label">{t("DASHBOARD.LOAN")}:</span><span className="mobile-card-value">{formatCurrency(day.loan)}</span></div>
                      <div className="mobile-card-row"><span className="mobile-card-label">{t("DASHBOARD.TOTAL")}:</span><span className="mobile-card-value">{formatCurrency(day.depositAmount)}</span></div>
                    </div>
                  );
                })}
                <div className="mobile-card total">
                  <div className="mobile-card-header">{t("DASHBOARD.WEEKLY_TOTAL")}</div>
                  <div className="mobile-card-row"><span className="mobile-card-label">{t("DASHBOARD.EOD")}:</span><span className="mobile-card-value">{formatCurrency(weeklyTotals.eod)}</span></div>
                  <div className="mobile-card-row"><span className="mobile-card-label">{t("DASHBOARD.FEE")}:</span><span className="mobile-card-value">{formatCurrency(weeklyTotals.fee)}</span></div>
                  <div className="mobile-card-row"><span className="mobile-card-label">{t("DASHBOARD.LOAN")}:</span><span className="mobile-card-value">{formatCurrency(weeklyTotals.loan)}</span></div>
                  <div className="mobile-card-row"><span className="mobile-card-label">{t("DASHBOARD.TOTAL")}:</span><span className="mobile-card-value">{formatCurrency(weeklyTotals.depositAmount)}</span></div>
                </div>
              </div>
            </Collapse>
          </>
        )}
      </div>
    </React.Fragment>
  );
}

export default Dashboard;
